import React, { useEffect } from "react";
import { useNode, useEditor } from "@craftjs/core";
import {
  MdDragIndicator,
  MdArrowUpward,
  MdArrowDownward,
  MdContentCopy,
  MdDeleteOutline,
} from "react-icons/md";

/**
 * RenderNode wraps each Craft.js component inside the canvas
 * to provide visual outlines, Move Up/Down buttons, Duplicate, and Delete actions.
 *
 * Individual components handle their own connect(drag(ref)) — this wrapper
 * provides the floating action toolbar and visual states only.
 */
export const RenderNode = ({ render }) => {
  const { id } = useNode();
  const { actions, query, isActive } = useEditor((state, query) => ({
    isActive: query.getEvent("selected").contains(id),
  }));

  const {
    isHover,
    dom,
    name,
    moveable,
    deletable,
    parent,
  } = useNode((node) => ({
    isHover: node.events.hovered,
    dom: node.dom,
    name:
      node.data.custom?.displayName ||
      node.data.displayName ||
      node.data.name ||
      "Element",
    moveable: query.node(node.id).isDraggable(),
    deletable: query.node(node.id).isDeletable(),
    parent: node.data.parent,
  }));

  const { connectors: { connect, drag } } = useNode();

  useEffect(() => {
    if (dom) {
      if (isActive) {
        dom.classList.add("craft-node-selected");
      } else {
        dom.classList.remove("craft-node-selected");
      }
      if (isHover) {
        dom.classList.add("craft-node-hovered");
      } else {
        dom.classList.remove("craft-node-hovered");
      }
    }
  }, [dom, isActive, isHover]);

  const isRoot = id === "ROOT";

  const setNodeRef = React.useCallback(
    (ref) => {
      if (ref) {
        connect(ref);
      }
    },
    [connect]
  );

  // If this is the root canvas container, render directly without wrapper
  if (isRoot) {
    return render;
  }

  // Move node up or down in parent's children list
  const handleMove = (direction, e) => {
    e.stopPropagation();
    try {
      const parentNode = query.node(parent).get();
      if (!parentNode || !parentNode.data.nodes) return;
      const currentIndex = parentNode.data.nodes.indexOf(id);
      if (currentIndex === -1) return;

      if (direction === "up" && currentIndex > 0) {
        actions.move(id, parent, currentIndex - 1);
      } else if (
        direction === "down" &&
        currentIndex < parentNode.data.nodes.length - 1
      ) {
        actions.move(id, parent, currentIndex + 2);
      }
    } catch (err) {
      console.error("Failed to move node:", err);
    }
  };

  // Duplicate current node
  const handleDuplicate = (e) => {
    e.stopPropagation();
    try {
      const nodeTree = query.node(id).toNodeTree();
      const parentNode = query.node(parent).get();
      const currentIndex = parentNode?.data?.nodes?.indexOf(id) ?? -1;
      actions.addNodeTree(
        nodeTree,
        parent,
        currentIndex !== -1 ? currentIndex + 1 : undefined
      );
    } catch (err) {
      console.error("Duplicate node error:", err);
    }
  };

  // Delete current node
  const handleDelete = (e) => {
    e.stopPropagation();
    try {
      actions.delete(id);
    } catch (err) {
      console.error("Delete node error:", err);
    }
  };


  return (
    <div
      ref={setNodeRef}
      data-craft-node-id={id}
      className={`relative group/craft-node transition-all ${
        isActive
          ? "ring-2 ring-indigo-600 ring-offset-2 rounded-xl my-1.5 shadow-sm"
          : isHover
          ? "ring-1.5 ring-indigo-400/90 ring-offset-1 rounded-xl"
          : ""
      }`}
    >
      {/* Floating Action Bar on top of active or hovered node */}
      {(isActive || isHover) && (
        <div
          className={`absolute -top-3.5 left-3 z-30 flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold shadow-xl transition-all select-none animate-fadeIn ${
            isActive
              ? "bg-indigo-600 text-white ring-1 ring-indigo-700"
              : "bg-gray-900/95 text-gray-100 backdrop-blur-md ring-1 ring-white/10"
          }`}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Drag Handle Icon */}
          {moveable && (
            <span
              ref={drag}
              className='p-0.5 px-1 hover:bg-white/25 active:bg-white/35 rounded flex items-center gap-1 text-white select-none transition-colors cursor-grab'
              title='Drag to reorder section'
            >
              <MdDragIndicator size={14} />
              <span className='text-[10px] font-bold tracking-wide uppercase opacity-90 hidden sm:inline'>
                Drag
              </span>
            </span>
          )}

          <span className='truncate max-w-[130px] font-semibold text-white/95'>
            {name}
          </span>

          {isActive && (
            <div className='flex items-center gap-1 ml-1 border-l border-white/30 pl-1.5'>
              <button
                type='button'
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => handleMove("up", e)}
                className='p-0.5 px-1 hover:bg-white/20 rounded transition-colors'
                title='Move Up'
              >
                <MdArrowUpward size={13} />
              </button>
              <button
                type='button'
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => handleMove("down", e)}
                className='p-0.5 px-1 hover:bg-white/20 rounded transition-colors'
                title='Move Down'
              >
                <MdArrowDownward size={13} />
              </button>
              <button
                type='button'
                onMouseDown={(e) => e.stopPropagation()}
                onClick={handleDuplicate}
                className='p-0.5 px-1 hover:bg-white/20 rounded transition-colors'
                title='Duplicate Block'
              >
                <MdContentCopy size={13} />
              </button>
              {deletable && (
                <button
                  type='button'
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={handleDelete}
                  className='p-0.5 px-1 hover:bg-red-500 rounded text-red-200 hover:text-white transition-colors'
                  title='Delete Block'
                >
                  <MdDeleteOutline size={13} />
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {render}
    </div>
  );
};
